import { useCallback, useEffect, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { handleSessionExpiry } from '../utils/sessionGuard';
import { useAuth } from '../context/AuthContext';
import { logoutBackend } from '../apis/authApi';

const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
];

interface IdleTimeoutOptions {
  timeoutMs?: number;
  excludedPaths?: string[];
}

/**
 * Monitors user activity and triggers logout after a period of inactivity.
 * The idle timeout is driven by the backend's `session_inactivity_timeout_min` value,
 * but the frontend uses a slightly shorter duration to proactively log out
 * before the backend rejects requests.
 *
 * InfoSec Finding 8.6 — idle timeout after 60 minutes of inactivity.
 */
export function useIdleTimeout({
  timeoutMs = 55 * 60 * 1000,
  excludedPaths = ['/login', '/health'],
}: IdleTimeoutOptions = {}): void {
  const { instance } = useMsal();
  const { logout: authLogout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isExpiring = useRef(false);

  const isExcluded = excludedPaths.includes(location.pathname);

  const handleExpiry = useCallback(async () => {
    if (isExpiring.current) return;
    isExpiring.current = true;

    try {
      await logoutBackend();
    } catch {
      // Best-effort server logout; session will expire server-side regardless
    }

    await authLogout();
    await handleSessionExpiry(instance);
    navigate('/login', { replace: true });
  }, [instance, navigate, authLogout]);

  const resetTimer = useCallback(() => {
    if (isExpiring.current) return;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(handleExpiry, timeoutMs);
  }, [handleExpiry, timeoutMs]);

  useEffect(() => {
    if (isExcluded) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    resetTimer();

    for (const event of ACTIVITY_EVENTS) {
      document.addEventListener(event, resetTimer, { passive: true });
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      for (const event of ACTIVITY_EVENTS) {
        document.removeEventListener(event, resetTimer);
      }
    };
  }, [resetTimer, isExcluded]);
}
